import {
  Alert,
  Button,
  FormControl,
  FormField,
  IconButton,
  openDialog,
  Search,
  TabsHeader,
} from 'platform/components';
import {
  Box,
  Clickable,
  CSSDimension,
  getSize,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  Integer,
  Show,
  Text,
  useAvailableHeight,
  VStack,
} from 'platform/foundation';

import {Dispatch, FocusEvent, HTMLAttributes, SetStateAction, useId, useState} from 'react';
import {useFieldArray, useWatch} from 'react-hook-form';

import {isNil, isNotNil} from 'ramda';

import {AddableTitleExtensionFeature, TitleExtensionLanguage} from '@dms/api/sales';
import {DragHandle, SortableCardList} from '@dms/features/dnd';
import i18n from '@dms/i18n';
import {getLocaleLanguageLabel} from '@dms/shared';

import {
  appendTitleExtensionEquipmentFeatures,
  buildAvailableTitleExtensionFeatures,
  TitleExtensionEquipmentDraftItem,
} from '../utils/hydrateTitleExtensionEquipmentDraft';
import {AddEquipmentForm} from './AddEquipmentForm';
import {TitleExtensionEquipmentFormValues} from './titleExtensionEquipmentDraftSchema';
import {TitleExtensionEquipmentSidebar} from './TitleExtensionEquipmentSidebar';

interface TitleExtensionEquipmentFieldsProps {
  control: FormControl<TitleExtensionEquipmentFormValues>;
  languages: TitleExtensionLanguage[];
  activeLanguage?: TitleExtensionLanguage;
  defaultLanguage?: TitleExtensionLanguage;
  onLanguageChange: (language: string) => void;
  searchValue: string;
  onSearchValueChange: (value: string) => void;
  isEditable: boolean;
  isInteractionDisabled: boolean;
  addableFeatures: AddableTitleExtensionFeature[];
  isAddableFeaturesLoading: boolean;
  isAddableFeaturesError: boolean;
  onRetryAddableFeatures: VoidFunction;
  removedItems: TitleExtensionEquipmentDraftItem[];
  setRemovedItems: Dispatch<SetStateAction<TitleExtensionEquipmentDraftItem[]>>;
  onReset: VoidFunction;
  isResetting: boolean;
  onSeparatorPresetChange: (separator: string) => void;
}

const SORTABLE_CONTAINER_ID = 'title-extension-equipment';
const SETTINGS_FOOTER_RESERVED_HEIGHT: Integer = 20;
const MINIMUM_USABLE_TABLE_HEIGHT: Integer = 60;
const TABLE_BOTTOM_SPACING: Integer = 4;

// eslint-disable-next-line max-lines-per-function -- Pre-existing violation
export function TitleExtensionEquipmentFields(props: TitleExtensionEquipmentFieldsProps) {
  const {availableHeightRef, availableHeight} = useAvailableHeight<HTMLDivElement>();
  const shortNameInputIdPrefix = useId();
  const [focusedFeatureKey, setFocusedFeatureKey] = useState<string>();
  const {fields, remove, replace} = useFieldArray({control: props.control, name: 'items'});
  const items = useWatch({control: props.control, name: 'items'}) ?? [];
  const activeLanguageIndex = isNil(props.activeLanguage)
    ? -1
    : props.languages.indexOf(props.activeLanguage);
  const sortableItems = fields.map((field, originalIndex) => ({
    ...(items.at(originalIndex) ?? field),
    id: field.id,
    originalIndex,
  }));
  const normalizedSearchValue = props.searchValue.trim().toLocaleLowerCase();
  const isSearchActive = normalizedSearchValue.length > 0;
  const reservedFooterHeight = props.isEditable ? SETTINGS_FOOTER_RESERVED_HEIGHT : 0;
  const remainingTableHeight = availableHeight - reservedFooterHeight - TABLE_BOTTOM_SPACING;
  const tableMaxHeight: CSSDimension | undefined =
    remainingTableHeight >= MINIMUM_USABLE_TABLE_HEIGHT // eslint-disable-line no-nested-ternary -- Pre-existing violation
      ? props.isEditable
        ? `calc(${getSize(availableHeight)} - ${getSize(SETTINGS_FOOTER_RESERVED_HEIGHT)} - ${getSize(TABLE_BOTTOM_SPACING)})`
        : `calc(${getSize(availableHeight)} - ${getSize(TABLE_BOTTOM_SPACING)})`
      : undefined;
  const filteredItems = isSearchActive
    ? sortableItems.filter((item) => {
        const shortName =
          activeLanguageIndex >= 0
            ? (item.shortNames.at(activeLanguageIndex)?.shortName ?? '')
            : '';

        return (
          item.featureKey === focusedFeatureKey ||
          [item.name, shortName].some((value) =>
            value.toLocaleLowerCase().includes(normalizedSearchValue)
          )
        );
      })
    : sortableItems;

  const handleRemove = (index: number) => {
    const removedItem = items.at(index);

    if (isNotNil(removedItem)) {
      props.setRemovedItems((currentItems) => [
        ...currentItems.filter((item) => item.featureKey !== removedItem.featureKey),
        removedItem,
      ]);
      remove(index);
    }
  };

  const availableFeatures = buildAvailableTitleExtensionFeatures(
    props.addableFeatures,
    props.removedItems,
    items
  );

  const handleAdd = (featureKeys: string[]) => {
    const selectedFeatures = featureKeys
      .map((featureKey) => availableFeatures.find((feature) => feature.featureKey === featureKey))
      .filter(isNotNil);

    const nextItems = appendTitleExtensionEquipmentFeatures(
      items,
      selectedFeatures,
      props.removedItems,
      props.languages
    );

    replace(nextItems);
    props.setRemovedItems((currentItems) =>
      currentItems.filter((item) => !featureKeys.includes(item.featureKey))
    );
  };

  const handleOpenPicker = () => {
    openDialog(<AddEquipmentForm items={availableFeatures} onAdd={handleAdd} />, {
      title: i18n.t('entity.newVehicle.catalogue.addEquipment'),
      size: 'small',
    });
  };

  const tabs = props.languages.map((language) => ({
    id: language,
    title: [
      `${getLocaleLanguageLabel(language)} (${language.toUpperCase()})`,
      language === props.defaultLanguage ? i18n.t('general.labels.default') : undefined,
    ]
      .filter(isNotNil)
      .join(' · '),
    badge: {
      value: items.filter((item) =>
        item.shortNames.some(
          (shortName) =>
            shortName.language === language && (shortName.shortName ?? '').trim().length > 0
        )
      ).length,
      colorScheme: 'neutral' as const,
    },
  }));

  const renderEquipmentRow = (
    item: (typeof sortableItems)[number],
    _index: number,
    handleProps?: HTMLAttributes<unknown>
  ) => {
    const shortName =
      activeLanguageIndex >= 0 ? (item.shortNames.at(activeLanguageIndex)?.shortName ?? '') : '';
    const shortNameInputId = `${shortNameInputIdPrefix}-${item.id}-${props.activeLanguage}`;

    const handleRowBlur = (event: FocusEvent<HTMLDivElement>) => {
      if (
        !(event.relatedTarget instanceof Node) ||
        !event.currentTarget.contains(event.relatedTarget)
      ) {
        setFocusedFeatureKey(undefined);
      }
    };

    return (
      <Clickable
        width="100%"
        onFocus={() => setFocusedFeatureKey(item.featureKey)}
        onBlur={handleRowBlur}
      >
        <Box
          width="100%"
          paddingHorizontal={4}
          paddingVertical={2}
          borderBottom="1px solid"
          borderColor="general.separator"
          backgroundColor="palettes.white.10.100"
          data-testid={`title-extension-equipment-row-${item.featureKey}`}
        >
          <HStack spacing={3} align="flex-start" wrap="nowrap">
            <Show when={props.isEditable}>
              <HStack minHeight={8} align="center">
                <DragHandle {...handleProps} aria-label={i18n.t('general.actions.reorder')}>
                  <Icon value="action/drag_indicator" color="palettes.neutral.60.100" />
                </DragHandle>
              </HStack>
            </Show>

            <Box flex={1} minWidth={0}>
              <HStack minHeight={8} align="center">
                <Text overflowWrap="anywhere">{item.name}</Text>
              </HStack>
            </Box>

            <Show when={props.isEditable && activeLanguageIndex >= 0}>
              <Box width={[32, 36, 40, 40]} flexShrink={0}>
                <Box position="absolute" width={0} height={0} overflow="hidden">
                  <label htmlFor={shortNameInputId}>
                    {i18n.t('page.titleExtensionEquipment.labels.shortName')}
                  </label>
                </Box>
                <FormField
                  key={props.activeLanguage}
                  id={shortNameInputId}
                  control={props.control}
                  type="text"
                  name={`items.${item.originalIndex}.shortNames.${activeLanguageIndex}.shortName`}
                  maxLength={255}
                  isDisabled={props.isInteractionDisabled}
                />
              </Box>
            </Show>

            <Show when={!props.isEditable}>
              <Box width={[32, 36, 40, 40]} flexShrink={0}>
                <HStack minHeight={8} align="center">
                  <Text
                    color={shortName.length > 0 ? 'primary' : 'tertiary'}
                    overflowWrap="anywhere"
                  >
                    {shortName.length > 0
                      ? shortName
                      : i18n.t('page.titleExtensionEquipment.labels.noShortName')}
                  </Text>
                </HStack>
              </Box>
            </Show>

            <Show when={props.isEditable}>
              <HStack minHeight={8} align="center">
                <IconButton
                  icon="action/delete"
                  severity="danger"
                  aria-label={i18n.t('general.actions.remove')}
                  onClick={() => handleRemove(item.originalIndex)}
                  isDisabled={props.isInteractionDisabled}
                  data-testid={`title-extension-equipment-remove-${item.featureKey}`}
                />
              </HStack>
            </Show>
          </HStack>
        </Box>
      </Clickable>
    );
  };

  return (
    <VStack spacing={4}>
      <Show when={props.languages.length > 0}>
        <VStack spacing={3}>
          <TabsHeader
            variant="condensed"
            tabs={tabs}
            activeTabId={props.activeLanguage}
            onChange={props.onLanguageChange}
          />
          <Show when={props.languages.length > 1}>
            <Text size="small" color="secondary">
              {i18n.t('page.titleExtensionEquipment.labels.sharedOrderHint')}
            </Text>
          </Show>
        </VStack>
      </Show>

      <Grid columns={[1, 1, 1, 10]} spacing={6}>
        <GridItem span={[1, 1, 1, 7]}>
          <Box ref={availableHeightRef} paddingBottom={TABLE_BOTTOM_SPACING}>
            <Box
              backgroundColor="palettes.white.10.100"
              borderRadius="medium"
              boxShadow="elevation_1"
              overflow="hidden"
            >
              <VStack maxHeight={tableMaxHeight}>
                <Box
                  flexShrink={0}
                  padding={4}
                  borderBottom="1px solid"
                  borderColor="general.separator"
                >
                  <HStack justify="space-between" align="center" spacing={4} wrap>
                    <VStack spacing={1}>
                      <Heading size={4}>
                        {i18n.t('page.titleExtensionEquipment.labels.equipmentCodes')}
                      </Heading>
                      <Text size="small" color="secondary">
                        {i18n.t('page.titleExtensionEquipment.labels.reorderHint')}
                      </Text>
                    </VStack>
                    <Box width={['100%', '100%', 64, 64]}>
                      <Search
                        value={props.searchValue}
                        onChange={(value) => props.onSearchValueChange(value ?? '')}
                        placeholder={i18n.t('entity.newVehicle.authoring.searchEquipment')}
                        isDisabled={props.isInteractionDisabled}
                      />
                    </Box>
                  </HStack>
                </Box>

                <Box
                  flexShrink={0}
                  paddingHorizontal={4}
                  paddingVertical={2}
                  backgroundColor="palettes.neutral.10.100"
                  borderBottom="1px solid"
                  borderColor="general.separator"
                >
                  <HStack spacing={3} align="center">
                    <Show when={props.isEditable}>
                      <Box width={6} />
                    </Show>
                    <Box flex={1} minWidth={0}>
                      <Text size="xSmall" color="secondary" alternative>
                        {i18n.t('entity.newVehicle.labels.equipment').toLocaleUpperCase()}
                      </Text>
                    </Box>
                    <Box width={[32, 36, 40, 40]} flexShrink={0}>
                      <Text size="xSmall" color="secondary" alternative>
                        {i18n
                          .t('page.titleExtensionEquipment.labels.shortName')
                          .toLocaleUpperCase()}
                      </Text>
                    </Box>
                    <Show when={props.isEditable}>
                      <Box width={8} />
                    </Show>
                  </HStack>
                </Box>

                <Box minHeight={0} overflowY="auto">
                  <Show when={filteredItems.length > 0}>
                    <SortableCardList
                      withHandle={props.isEditable}
                      disabledDrag={
                        !props.isEditable || props.isInteractionDisabled || isSearchActive
                      }
                      disableDropAnimation
                      containerId={SORTABLE_CONTAINER_ID}
                      items={filteredItems}
                      onReorder={(reorderedItems) => {
                        if (isSearchActive) {
                          return;
                        }
                        replace(
                          reorderedItems.map((item) => ({
                            featureKey: item.featureKey,
                            name: item.name,
                            shortNames: item.shortNames,
                          }))
                        );
                      }}
                      renderCard={renderEquipmentRow}
                    />
                  </Show>
                  <Show when={filteredItems.length === 0}>
                    <Box padding={6}>
                      <Text align="center" color="secondary">
                        {i18n.t('general.labels.noResultsFound')}
                      </Text>
                    </Box>
                  </Show>
                </Box>

                <Show when={props.isEditable}>
                  <Box flexShrink={0} padding={4}>
                    <VStack spacing={3}>
                      <Show when={props.isAddableFeaturesError}>
                        <Alert
                          variant="error"
                          layout="detailed"
                          message={i18n.t(
                            'page.titleExtensionEquipment.labels.addableFeaturesLoadError'
                          )}
                          actions={[
                            {
                              variant: 'secondary',
                              title: i18n.t('general.actions.retry'),
                              onClick: props.onRetryAddableFeatures,
                            },
                          ]}
                        />
                      </Show>
                      <HStack justify="space-between" align="center" wrap spacing={3}>
                        <Button
                          type="button"
                          variant="link"
                          leftIcon="content/add_circle"
                          title={i18n.t('entity.newVehicle.catalogue.addEquipment')}
                          onClick={handleOpenPicker}
                          isLoading={props.isAddableFeaturesLoading}
                          isDisabled={
                            props.isInteractionDisabled ||
                            props.isAddableFeaturesError ||
                            availableFeatures.length === 0
                          }
                        />
                        <Button
                          type="button"
                          variant="dangerLink"
                          leftIcon="navigation/refresh"
                          title={i18n.t('page.titleExtensionEquipment.actions.resetToDefaults')}
                          isLoading={props.isResetting}
                          isDisabled={props.isInteractionDisabled}
                          onClick={props.onReset}
                        />
                      </HStack>
                    </VStack>
                  </Box>
                </Show>
              </VStack>
            </Box>
          </Box>
        </GridItem>

        <GridItem span={[1, 1, 1, 3]}>
          <TitleExtensionEquipmentSidebar
            control={props.control}
            activeLanguage={props.activeLanguage}
            isEditable={props.isEditable}
            isInteractionDisabled={props.isInteractionDisabled}
            onSeparatorPresetChange={props.onSeparatorPresetChange}
          />
        </GridItem>
      </Grid>
    </VStack>
  );
}
